import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";
import FormErrors from "@/components/FormErrors/FormErrors.vue";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";

import { v4 as uuidv4 } from "uuid";
import { JSConn } from "@/utilities/js-conn";

import { NamedRts } from "@/router";
import { buildSubmitButton, buildFormErrors } from "@/utilities/form-helpers";

import { TOAST_OPTIONS } from "@/constants";

import { trucksController } from "@/controllers/trucks";

import { buildFormTruck, prettifyTruckStatus, Truck } from "@/models/Truck";

export default defineComponent({
  name: "truckEditForm",
  components: {
    FormErrors,
    LoadingAnimation,
  },
  props: {
    truckId: {
      type: String,
      required: true,
    },
  },

  setup(props, ctx) {
    let $store = ctx.root.$store;

    let dataLoaded = ref(false);
    let loadingMessage = ref("Preparing the form...");

    let firstInput: any = ref(null);

    let deleteModal: any = ref(null);

    const showDeleteModal = () => {
      deleteModal.value.show();
    };

    const hideDeleteModal = () => {
      deleteModal.value.hide();
    };

    const deleting = ref(false);

    const deleteTruck = () => {
      const jsConn = new JSConn();
      deleting.value = true;

      jsConn.formParams = {
        id: props.truckId,
      };

      trucksController.deleteTruck(jsConn).then(() => {
        ctx.root.$router.push({
          name: NamedRts.trucks,
        });
        return Promise.resolve();
      });
    };

    const cancelParams = () => {
      return {
        name: NamedRts.trucks,
      };
    };

    let {
      formTruck,
      truckStatuses,
      loadTruckStatusesIntoForm,
      loadTruckIntoForm,
    } = buildFormTruck();

    let { submitButtonText, submitting } = buildSubmitButton();
    let { hasFormErrors, formErrors } = buildFormErrors();

    const apiTruck = computed(() => {
      return $store.getters.truck as Truck;
    });

    onMounted(() => {
      let dataPromises = [];
      let jsConn = new JSConn();

      loadTruckStatusesIntoForm(truckStatuses);

      if (props.truckId !== "new") {
        jsConn.formParams = {
          id: props.truckId,
        };

        dataPromises.push(trucksController.needTruck(jsConn));
      }

      Promise.all(dataPromises)
        .then(() => {
          if (props.truckId !== "new") {
            loadTruckIntoForm(apiTruck.value, formTruck);
          }
        })
        .finally(() => {
          // at this point, check to see if there is a phone
          // number and load an empty one if there are no phone numbers
          dataLoaded.value = true;
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
        });
    });

    const onSubmit = (evt: any) => {
      evt.preventDefault();
      const jsConn = new JSConn();

      hasFormErrors.value = false;
      formErrors.value = [];
      submitting.value = true;
      submitButtonText.value = "Submitting...";

      let storeDispatch;
      if (props.truckId === "new") {
        formTruck.id = uuidv4();
        jsConn.formParams = formTruck;
        storeDispatch = trucksController.createTruck;
      } else {
        jsConn.formParams = formTruck;
        storeDispatch = trucksController.updateTruck;
      }

      storeDispatch(jsConn)
        .then(() => {
          ctx.root.$bvToast.toast(
            "Truck was successfully saved",
            TOAST_OPTIONS,
          );
          ctx.root.$router.push(cancelParams());
        })
        .catch(() => {
          hasFormErrors.value = true;
          formErrors.value = jsConn.error["response"]["data"];
        })
        .finally(() => {
          submitting.value = false;
          submitButtonText.value = "Submit";
        });
    };

    return {
      dataLoaded,
      loadingMessage,
      firstInput,
      hasFormErrors,
      formErrors,

      submitButtonText,
      submitting,
      onSubmit,

      cancelParams,

      deleteModal,
      showDeleteModal,
      hideDeleteModal,
      deleteTruck,
      deleting,

      apiTruck,
      formTruck,
      truckStatuses,
      prettifyTruckStatus,
    };
  },
});
